<script context="module" lang="ts">
  export type AvatarProps = {
    avatarUrl: string;
    title: string;
    size: 'small' | 'medium';
    border: 'small' | 'medium';
  };
  export let sizes = ['small', 'medium', 'large', 'x-large'];
  export let fontSizes = ['text-xs', 'text-sm', 'text-base'];
  export let variants = ['default', 'outlined', 'text'];
  export let colours = ['primary', 'secondary', 'none', 'info', 'success', 'warning', 'error'];
  export let iconPositions = ['left', 'right'];
</script>

<script lang="ts">
  import { popup } from '@skeletonlabs/skeleton';
  import { type IconType, Icons } from '$lib/components/common/icon';
  import Avatar from '$lib/components/common/avatar/Avatar.svelte';
  import Tooltip from '$lib/components/common/tooltip/Tooltip.svelte';
  import { GetHoverPopupSettings } from '$lib/components/common/tooltip/utils';
  import { twMerge } from 'tailwind-merge';

  export let label: string | null;
  export let disabled = false;
  export let size: (typeof sizes)[number] = 'small';
  export let fontSize: (typeof fontSizes)[number] = 'text-sm';
  export let variant: (typeof variants)[number] = 'default';
  export let active = false;
  export let colour: (typeof colours)[number] = 'primary';

  let classname = '';
  export { classname as class };
  export let icon: IconType | null = null;
  export let iconNumberOfPaths: number = 0;
  export let iconPosition: (typeof iconPositions)[number] = 'left';
  export let iconClass = '';
  export let avatar: AvatarProps | null = null;
  export let onClick = () => {};
  export let tooltip: string | undefined = undefined;
  export let iconSize = '14px';
  export let dataTestid = '';
  let tooltipSettings = GetHoverPopupSettings(label || 'example');

  $: outlinedClass = '';
  $: {
    if (variant == 'outlined') {
      outlinedClass = `bg-initial text-[--text2] variant-outlined border border-solid bg-surface-50
      ${active ? 'text-primary-500 border-primary-500' : 'border-surface-600'}
      ${
        !disabled &&
        `hover:brightness-100
        hover:!bg-surface-500
        hover:!text-primary-500
        focus:border-tertiary-600
        focus:outline-tertiary-600
        focus:outline-1
        focus:outline `
      }`;
    }
  }
</script>

<button
  data-testid={dataTestid}
  class={twMerge(
    `alii-button btn flex h-[32px] cursor-pointer items-center justify-center px-4 py-3 font-medium
  tracking-normal text-surface-50`,
    size === 'medium' && 'min-w-[113px]',
    size === 'large' && 'h-[40px] min-w-[253px] px-[30px] py-[12px]',
    size === 'x-large' && 'h-[44px] min-w-[113px]',
    fontSize,
    outlinedClass,
    classname
  )}
  class:active
  class:disabled
  class:variant-filled-primary={variant == 'default' && colour === 'primary'}
  class:variant-filled-secondary={variant == 'default' && colour === 'secondary'}
  class:variant-filled-success={variant == 'default' && colour === 'success'}
  class:variant-filled-warning={variant == 'default' && colour === 'warning'}
  class:variant-filled-error={variant == 'default' && colour === 'error'}
  class:variant-filled-tertiary={variant == 'default' && colour === 'info'}
  class:variant-filled-none={variant == 'default' && colour === 'none'}
  {disabled}
  use:popup={tooltipSettings}
  on:click={onClick}
>
  {#if icon && iconPosition === 'left'}
    <span>
      <svelte:component this={Icons[icon]} size={iconSize} class={twMerge(iconClass)} numberofPaths={iconNumberOfPaths} />
    </span>
  {/if}
  {#if avatar}
    <span class="m-0">
      <Avatar avatarUrl={avatar.avatarUrl} title={avatar.title} size={avatar.size} border={avatar.border}></Avatar>
    </span>
  {/if}
  {#if label}
    <span class={iconPosition}>
      {label}
    </span>
  {/if}
  {#if icon && iconPosition === 'right'}
    <span class={avatar ? '!ml-auto' : 'ml-0'}>
      <svelte:component this={Icons[icon]} size={iconSize} class={twMerge(iconClass)} numberofPaths={iconNumberOfPaths} />
    </span>
  {/if}
</button>

{#if tooltip}
  <Tooltip config={tooltipSettings}>{tooltip}</Tooltip>
{/if}

<style lang="postcss">
  @import '$lib/theme/_colours.postcss';

  .alii-button {
    &.left {
      margin-left: 5px;
    }
    &.right {
      margin-right: 5px;
    }
    &.variant-filled-secondary {
      @apply bg-secondary-500 text-primary-500;
    }
    &.variant-filled-none {
      @apply bg-secondary-500 text-primary-500;
    }
    &.variant-outlined {
      &.active {
        @apply text-primary-500;
      }
      &.disabled {
        @apply border-none bg-transparent text-secondary-500 !opacity-100;
      }
    }
    &.disabled {
      @apply bg-surface-600 text-[--text2] !opacity-100;
    }
    &.disabled {
      cursor: not-allowed;
    }
  }
</style>
